import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import { ZAttrMeta } from "src/business/attribute";
import {
  ClassesFilter,
  classesLoader2,
  loadClsBranch,
  ZClassRow,
} from "src/business/classes";
import { loadClassifier, ZClassifier } from "src/business/classifier";
import { loadSchemes, ZSpecification } from "src/business/specification";
import { RemoteData } from "src/common/RemoteData";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import { PageUrl } from "src/routes/PageUrl";

import { buildClassColumns } from "../../../business/classes/buildClassColumns";
import { ClassesTreeStore } from "../../../business/classes/ClassesTree";

export type ClsPageData = {
  classifier: ZClassifier;
  tableStore: RtStore<ZClassRow, ClassesFilter>;
  treeStore: ClassesTreeStore;
  schemes: ZSpecification[];
};

export const classesPageStore = makeAutoObservable({
  data: { status: "none" } as RemoteData<ClsPageData>,
  setData(newData: RemoteData<ClsPageData>) {
    this.data = newData;
  },

  dispose() {
    this.setData({ status: "none" });
  },

  get pageTitle(): string {
    return this.data.status === "ready"
      ? this.data.result.classifier.name
      : "...";
  },
  get crumbs(): BreadcrumbItem[] {
    return [
      { title: "Классификаторы", page: PageUrl.classifiers },
      { title: this.pageTitle },
    ];
  },
  async init(classifierId: string) {
    try {
      this.setData({ status: "wait" });
      const [classifier, root, schemes] = await Promise.all([
        loadClassifier(classifierId),
        loadClsBranch(classifierId),
        loadSchemes(classifierId),
      ]);
      const tableStore = new RtStore<ZClassRow, ClassesFilter>({
        rowKey: "id",
        loader: classesLoader2(classifierId, schemes),
        defaultRequest: { pageSize: 20 },
      });
      const treeStore = new ClassesTreeStore(root);
      this.setData({
        status: "ready",
        result: { classifier, tableStore, treeStore, schemes },
      });
    } catch (error) {
      this.setData({ status: "error", error });
    }
  },

  get attributesMeta(): ZAttrMeta[] {
    const { data } = this;
    if (data.status !== "ready") return [];
    return data.result.schemes.flatMap(
      ({ definition }) => definition?.attributes ?? [],
    );
  },
  get columns() {
    const { data } = this;
    const [schemes, displayAttribute] =
      data.status === "ready"
        ? [data.result.schemes, data.result.classifier.displayAttribute]
        : [[]];
    return buildClassColumns(schemes, displayAttribute);
  },
});
